import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  ChevronDown,
  FileText,
  IndianRupee,
  LayoutDashboard,
  LogOut,
  Package,
  ShoppingCart,
  Warehouse,
} from "lucide-react";
import PanelLayout from "@/components/shared/panel/PanelLayout";
import type { PanelNavEntry } from "@/components/shared/panel/PanelSidebar";
import NotificationBell from "@/components/shared/notifications/NotificationBell";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/useLogout";
import { getInitials } from "@/lib/account-profile-utils";
import { channelPartiesService } from "@/services/channelPartiesService";
import { channelOrdersService } from "@/services/channelOrdersService";
import { ChannelDeskProvider, distributorDesk, type ChannelDesk } from "@/lib/channel-desk";

const pageTitles: Record<string, string> = {
  "": "Overview",
  "/catalogue": "Catalogue",
  "/stock": "Stock",
  "/orders": "Orders",
  "/commission": "Commission",
  "/reports": "Reports",
  "/notifications": "Notifications",
};

export function ChannelDeskLayout({ desk }: { desk: ChannelDesk }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const logout = useLogout();
  const profile = useQuery({
    queryKey: ["channel", "me"],
    queryFn: () => channelPartiesService.getMine(),
  });
  const outgoing = useQuery({
    queryKey: ["channel", "orders", "outgoing"],
    queryFn: () => channelOrdersService.outgoing(),
  });
  const pending = (outgoing.data ?? []).filter((order) => order.statusText === "Pending").length;

  const displayName = profile.data?.contactName || user?.name || desk.role;
  const displayEmail = profile.data?.email || user?.email || "";
  const initials = getInitials(displayName);
  const suffix = pathname.slice(desk.base.length);
  const pageTitle = suffix.startsWith("/catalogue/place-order")
    ? "Place Order"
    : suffix.startsWith("/reports")
      ? "Reports"
      : (pageTitles[suffix] ?? desk.role);

  const nav: PanelNavEntry[] = [
    { title: "Overview", url: desk.base, icon: LayoutDashboard },
    { title: "Catalogue", url: `${desk.base}/catalogue`, icon: Package },
    { title: "Stock", url: `${desk.base}/stock`, icon: Warehouse },
    { title: "Orders", url: `${desk.base}/orders`, icon: ShoppingCart, badge: pending || undefined },
    { title: "Commission", url: `${desk.base}/commission`, icon: IndianRupee },
    { title: "Reports", url: `${desk.base}/reports`, icon: FileText },
    { title: "Notifications", url: `${desk.base}/notifications`, icon: Bell },
  ];

  const topbarActions = (
    <>
      <NotificationBell scope="me" viewAllPath={`${desk.base}/notifications`} />
      <Separator orientation="vertical" className="mx-0.5 hidden h-7 sm:block" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="glass-chip h-10 gap-2 rounded-xl px-1.5 hover:bg-transparent sm:px-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-primary-foreground ring-2 ring-primary/20">
              {initials}
            </div>
            <div className="hidden flex-col items-start leading-tight lg:flex">
              <span className="max-w-[120px] truncate text-[13px] font-semibold text-foreground">{displayName}</span>
              <span className="text-[10px] text-muted-foreground">{desk.role}</span>
            </div>
            <ChevronDown className="hidden h-3 w-3 text-muted-foreground sm:block" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" sideOffset={8} collisionPadding={8} className="z-50 w-[min(220px,calc(100vw-1rem))] rounded-xl border border-border bg-popover p-1.5 shadow-2xl">
          <div className="mb-1 rounded-lg bg-secondary/40 px-2.5 py-3">
            <p className="text-sm font-bold text-foreground">{displayName}</p>
            <p className="truncate text-[11px] text-muted-foreground">{displayEmail || profile.data?.businessName}</p>
          </div>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
            <Link to={`${desk.base}/commission`}>
              <IndianRupee className="h-4 w-4 text-muted-foreground" /> Commission
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm" asChild>
            <Link to={`${desk.base}/notifications`}>
              <Bell className="h-4 w-4 text-muted-foreground" /> Notifications
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="my-1" />
          <DropdownMenuItem
            className="cursor-pointer gap-2.5 rounded-lg py-2 text-sm text-destructive focus:bg-destructive/10 focus:text-destructive"
            onClick={() => void logout()}
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <ChannelDeskProvider desk={desk}>
      <PanelLayout
        brand={{
          icon: Warehouse,
          title: profile.data?.businessName ?? desk.role,
          subtitle: `${desk.role} Panel`,
          url: desk.base,
        }}
        navItems={nav}
        footer={{ label: "Sign Out", url: "/" }}
        breadcrumbPrefix={desk.role}
        pageTitle={pageTitle}
        sectionLabel="MENU"
        topbarActions={topbarActions}
      />
    </ChannelDeskProvider>
  );
}

const DistributorLayout = () => <ChannelDeskLayout desk={distributorDesk} />;

export default DistributorLayout;
